"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Link from "next/link";
import Image from "next/image";

interface RegisterFormData {
  companyName: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 40);
}

export default function RegisterPage() {
  const router = useRouter();
  const [formData, setFormData] = useState<RegisterFormData>({
    companyName: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field: keyof RegisterFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    if (
      !formData.companyName ||
      !formData.name ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      toast.error("Vul alle velden in");
      return false;
    }

    const slug = toSlug(formData.companyName);
    if (slug.length < 3) {
      toast.error("Bedrijfsnaam moet minimaal 3 bruikbare karakters bevatten");
      return false;
    }

    if (!formData.email.includes("@")) {
      toast.error("Voer een geldig email-adres in");
      return false;
    }

    if (formData.password.length < 8) {
      toast.error("Wachtwoord moet minimaal 8 karakters zijn");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Wachtwoorden komen niet overeen");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantSlug: toSlug(formData.companyName),
          ownerEmail: formData.email,
          ownerPassword: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || "Registratie mislukt");
        return;
      }

      const baseDomain = process.env.NEXT_PUBLIC_BASE_DOMAIN ?? "mijnpatient.nl";
      const slug = toSlug(formData.companyName);
      const tenantLoginUrl = `https://${slug}.${baseDomain}/login`;

      toast.success(
        "Account succesvol aangemaakt! Je wordt doorgestuurd naar de inlogpagina...",
      );

      // Redirect to tenant subdomain login after 2 seconds
      setTimeout(() => {
        window.location.href = tenantLoginUrl;
      }, 2000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Er is een fout opgetreden");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left Column - Form */}
      <div className="flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary" />
            <span className="text-xl font-semibold">MijnPatient</span>
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">
              Maak een account aan
            </h1>
            <p className="text-neutral-600">
              Registreer nu om je praktijk vandaag nog te stroomlijnen
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="companyName">Bedrijfsnaam</Label>
              <Input
                id="companyName"
                type="text"
                placeholder="Bedrijfsnaam"
                value={formData.companyName}
                onChange={(e) => handleChange("companyName", e.target.value)}
                required
                disabled={isLoading}
              />
              <p className="text-xs text-neutral-500">
                Dit wordt je URL:{" "}
                <span className="font-medium text-neutral-700">
                  {toSlug(formData.companyName) || "jouw-naam"}.mijnpatient.nl
                </span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Naam</Label>
              <Input
                id="name"
                type="text"
                placeholder="Jan Jansen"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="jan@praktijk.nl"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bevestig wachtwoord</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleChange("confirmPassword", e.target.value)
                }
                required
                disabled={isLoading}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? "Account aanmaken..." : "Registreren"}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-neutral-500">
                Of registreer met
              </span>
            </div>
          </div>

          {/* OAuth Buttons */}
          <div className="grid grid-cols-2 gap-4">
            <Button variant="outline" type="button" disabled>
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </Button>
            <Button variant="outline" type="button" disabled>
              <svg
                className="mr-2 h-4 w-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
              </svg>
              Apple
            </Button>
          </div>

          {/* Sign In Link */}
          <div className="text-center text-sm text-neutral-600">
            Heb je al een account?{" "}
            <Link
              href="/login"
              className="font-medium text-primary hover:underline"
            >
              Inloggen
            </Link>
          </div>
        </div>
      </div>

      {/* Right Column - Hero */}
      <div className="hidden bg-primary p-12 text-white lg:flex lg:flex-col lg:justify-center">
        <div className="space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl font-bold leading-tight">
              Beheer je team en praktijk moeiteloos
            </h2>
            <p className="text-lg text-primary-foreground/80">
              Log in om toegang te krijgen tot je CRM-dashboard en je team te
              beheren.
            </p>
          </div>

          {/* Dashboard Preview Card */}
          <Card className="border-none bg-white/10 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-primary-foreground/60">
                      Totale sales
                    </p>
                    <p className="text-2xl font-bold">€189.374</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-primary-foreground/60">
                      Conversie
                    </p>
                    <p className="text-2xl font-bold">00:01:30</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-primary-foreground/60">
                      Sales overzicht
                    </p>
                    <div className="text-sm">Weekly</div>
                  </div>
                </div>

                {/* Chart Placeholder */}
                <div className="h-32 rounded-lg bg-white/20 backdrop-blur-sm" />

                {/* Transactions Sample */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-primary-foreground/60">
                    Recente transacties
                  </p>
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between rounded-lg bg-white/10 p-2 text-sm"
                      >
                        <span>Product transactie #{i}</span>
                        <span className="font-medium">€25.684</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trial Info */}
          <div className="rounded-lg border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-white/20 p-2">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <p className="font-semibold">Gratis 2 weken proberen</p>
                <p className="text-sm text-primary-foreground/80">
                  Geen creditcard vereist. Start direct met het beheren van je
                  praktijk.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 flex items-center justify-between text-sm text-primary-foreground/60">
          <p>Copyright © 2025 MijnPatient</p>
          <Link href="/privacy" className="hover:text-white">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
